import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress, Avatar,
    Modal, ModalDialog, ModalClose, IconButton, Divider,
    Textarea,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ReceiptLongRoundedIcon       from '@mui/icons-material/ReceiptLongRounded';
import PointOfSaleRoundedIcon       from '@mui/icons-material/PointOfSaleRounded';
import PaymentsRoundedIcon          from '@mui/icons-material/PaymentsRounded';
import CreditCardRoundedIcon        from '@mui/icons-material/CreditCardRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import LocationOnRoundedIcon        from '@mui/icons-material/LocationOnRounded';
import PersonRoundedIcon            from '@mui/icons-material/PersonRounded';
import AccessTimeRoundedIcon        from '@mui/icons-material/AccessTimeRounded';
import LogoutRoundedIcon            from '@mui/icons-material/LogoutRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import ArrowForwardRoundedIcon      from '@mui/icons-material/ArrowForwardRounded';
import BlockRoundedIcon             from '@mui/icons-material/BlockRounded';
import GavelRoundedIcon             from '@mui/icons-material/GavelRounded';
import BarChartRoundedIcon          from '@mui/icons-material/BarChartRounded';
import RefreshRoundedIcon           from '@mui/icons-material/RefreshRounded';
import AddRoundedIcon               from '@mui/icons-material/AddRounded';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import RestoreFromTrashRoundedIcon from '@mui/icons-material/RestoreFromTrashRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIWorkstationStats,
    APIWorkstationGetAdmins, APIWorkstationStartShift,
    APIGetPendingSaleNotifications, APIGetOrders,
    APIProcessSaleNotification, APICancelSaleNotification,
    APIRestoreSaleNotification, APICreateOrderDeletionRequest,
    parseApiError, APIGetProducts, APIGetDiscounts, APIGetProductCategories,
    APIDeleteOrder, APICancelOrder
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import WorkstationAddOrderModal from './WorkstationAddOrderModal';
import ProcessSaleModal from './ProcessSaleModal';
import { businessTodayISO, businessDayISO } from '../../businessDay';
import CompactCheckCard from '../../components/CompactCheckCard';


// ─── Утилиты ──────────────────────────────────────────────────────────────────
const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

// "Сегодня 20:45" / "Вчера 20:45" / "18.06 20:45"
const fmtDateTimeRelative = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

    const startOfDay = (date) => {
        const x = new Date(date);
        x.setHours(0, 0, 0, 0);
        return x;
    };
    const todayMid = startOfDay(new Date());
    const targetMid = startOfDay(d);
    const diffDays = Math.round((todayMid - targetMid) / 86_400_000);

    if (diffDays === 0) return `Сегодня ${time}`;
    if (diffDays === 1) return `Вчера ${time}`;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm} ${time}`;
};

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const MONEY_META = {
    transfer: { label: 'Перевод',   color: '#818cf8', bg: 'rgba(129,140,248,0.10)', icon: <CreditCardRoundedIcon  sx={{ fontSize: 14 }} /> },
    cash:     { label: 'Наличные',  color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   icon: <PaymentsRoundedIcon    sx={{ fontSize: 14 }} /> },
    terminal: { label: 'Терминал',  color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} /> },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.06 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

// ════════════════════════════════════════════════════════════════════
//  Подкомпонент: выбор админа для начала смены
// ════════════════════════════════════════════════════════════════════
function StartShiftPanel({ onStart }) {
    const { token, showToast } = useContext(AppContext);
    const [admins,   setAdmins]   = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [selected, setSelected] = useState(null);
    const [starting, setStarting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const data = await APIWorkstationGetAdmins(token);
        if (Array.isArray(data?.admins)) setAdmins(data.admins);
        else if (Array.isArray(data))    setAdmins(data);
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const handleStart = async () => {
        if (!selected) { showToast('Выбери себя из списка', 'warning'); return; }
        setStarting(true);
        const res = await onStart(selected);
        setStarting(false);
        if (!res?.ok) {
            // toast уже показан в контексте
        }
    };

    return (
        <Box sx={{
            bgcolor: 'var(--joy-palette-background-level1)',
            border: '1px solid var(--joy-palette-divider)',
            borderRadius: 'var(--joy-radius-xl)',
            p: { xs: 2, sm: 3 },
        }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 0.5 }}>
                Начать смену
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                Выберите себя из списка администраторов на сегодня
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <MemeLoader />
                </Box>
            ) : admins.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <BlockRoundedIcon sx={{ fontSize: 36, color: 'var(--joy-palette-text-tertiary)', mb: 1 }} />
                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                        Сегодня нет назначенных администраторов
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={1}>
                    {admins.map(a => {
                        const isSel = selected === a.id;
                        return (
                            <Box
                                key={a.id}
                                onClick={() => setSelected(a.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 2, py: 1.4,
                                    borderRadius: 'var(--joy-radius-lg)',
                                    border: '1px solid',
                                    borderColor: isSel ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                    bgcolor: isSel ? 'rgba(162,38,226,0.08)' : 'var(--joy-palette-background-level2)',
                                    cursor: 'pointer', transition: 'all .15s',
                                    '&:hover': { borderColor: 'var(--joy-palette-primary-400)' },
                                }}
                            >
                                <Avatar src={a.avatar_url} size="sm" sx={{
                                    width: 36, height: 36, fontSize: 12, fontWeight: 700,
                                    bgcolor: 'rgba(162,38,226,0.15)',
                                    color: 'var(--joy-palette-primary-300)',
                                    border: '1.5px solid var(--joy-palette-primary-500)',
                                }}>
                                    {(a.account_name || a.full_name || '').charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                        {a.full_name || a.name}
                                    </Typography>
                                    {a.location_name && (
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                            {a.location_name}
                                        </Typography>
                                    )}
                                </Box>
                                {isSel && <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />}
                            </Box>
                        );
                    })}
                </Stack>
            )}

            <Button
                fullWidth
                disabled={!selected || starting}
                loading={starting}
                onClick={handleStart}
                sx={{
                    mt: 2,
                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                    color: '#fff', fontWeight: 700, fontSize: 14,
                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                    '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                }}
            >
                Начать смену
            </Button>
        </Box>
    );
}

// ════════════════════════════════════════════════════════════════════
//  Подкомпонент: карточка pending-уведомления (быстрое оформление)
// ════════════════════════════════════════════════════════════════════
function PendingSaleCard({ notification, onProcess, onCancel, onRestore, mode = 'pending', processing }) {
    const items = notification.items || [];
    const total = parseFloat(notification.total_value || 0);

    // 'cancelled' — серая рамка, без кнопок процессинга, с кнопкой «Вернуть»
    const isCancelled = mode === 'cancelled';
    const accent = isCancelled ? '#94a3b8' : '#f59e0b';
    const accentBg = isCancelled ? 'rgba(148,163,184,0.06)' : 'rgba(245,158,11,0.06)';
    const accentBorder = isCancelled ? 'rgba(148,163,184,0.35)' : 'rgba(245,158,11,0.35)';

    return (
        <Box sx={{
            border: `1px solid ${accentBorder}`,
            bgcolor: accentBg,
            borderRadius: 'var(--joy-radius-lg)',
            p: 1.8,
            transition: 'all .15s',
            opacity: isCancelled ? 0.85 : 1,
        }}>
            {/* Шапка */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                <Avatar src={notification.photographer_avatar_url} size="sm" sx={{
                    width: 32, height: 32, fontSize: 11, fontWeight: 700,
                    bgcolor: `${accent}26`,
                    color: accent,
                    border: `1.5px solid ${accent}`,
                    flexShrink: 0,
                }}>
                    {(notification.photographer_name || '').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                        {notification.photographer_name}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                        Добавлен: {fmtDateTimeRelative(notification.date_create)}
                        {isCancelled && notification.processed_at && (
                            <> · отменён {fmtTime(notification.processed_at)}</>
                        )}
                    </Typography>
                </Box>
                <Typography sx={{
                    fontSize: 18, fontWeight: 800,
                    color: accent,
                    fontVariantNumeric: 'tabular-nums',
                    textDecoration: isCancelled ? 'line-through' : 'none',
                }}>
                    {fmtMoney(total)} ₽
                </Typography>
            </Box>

            {/* Состав чека */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: notification.comment ? 1 : 1.5 }}>
                {items.map((it, idx) => {
                    const meta = MONEY_META[it.money_type] || MONEY_META.transfer;
                    return (
                        <Chip key={idx} size="sm" variant="soft"
                            startDecorator={meta.icon}
                            sx={{
                                fontSize: 11, fontWeight: 700,
                                bgcolor: meta.bg, color: meta.color,
                            }}
                        >
                            {fmtMoney(it.value)} ₽ · {meta.label}
                        </Chip>
                    );
                })}
            </Box>

            {/* Комментарий */}
            {notification.comment && (
                <Box sx={{
                    bgcolor: 'var(--joy-palette-background-level2)',
                    borderRadius: 'var(--joy-radius-md)',
                    px: 1.5, py: 1, mb: 1.5,
                    borderLeft: `3px solid ${accent}`,
                }}>
                    <Typography sx={{
                        fontSize: 12, color: 'var(--joy-palette-text-primary)',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                        {notification.comment}
                    </Typography>
                </Box>
            )}

            {/* Кнопки */}
            {isCancelled ? (
                <Button
                    size="sm" fullWidth
                    startDecorator={<RestoreFromTrashRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onRestore(notification)}
                    disabled={processing}
                    sx={{
                        background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                        color: '#fff', fontWeight: 700, fontSize: 12,
                        '&:hover': { boxShadow: '0 4px 12px rgba(162,38,226,0.4)' },
                    }}
                >
                    Восстановить
                </Button>
            ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="sm"
                        startDecorator={<ReceiptLongRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={() => onProcess(notification)}
                        disabled={processing}
                        sx={{
                            flex: 1,
                            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                            color: '#fff', fontWeight: 700, fontSize: 12,
                            '&:hover': { boxShadow: '0 4px 12px rgba(34,197,94,0.4)' },
                        }}
                    >
                        Подтвердить
                    </Button>
                    <IconButton
                        size="sm" variant="outlined" color="danger"
                        onClick={() => {
                            if (window.confirm('Отменить продажу? Чек будет удалён из кассы, выручка пересчитается.')) {
                                onCancel(notification);
                            }
                        }}
                        disabled={processing}
                        sx={{
                            bgcolor: 'var(--joy-palette-background-level2)',
                            border: '1px solid var(--joy-palette-divider)',
                            '&:hover': { borderColor: 'var(--joy-palette-danger-400)' },
                        }}
                    >
                        <BlockRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}

// ════════════════════════════════════════════════════════════════════
//  WorkstationDashboard
// ════════════════════════════════════════════════════════════════════
export default function WorkstationDashboard() {
    const { token, showToast, onRefreshTick, activeShift, startShift, endShift, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [addOrderOpen, setAddOrderOpen] = useState(false);

    const [stats,     setStats]     = useState(null);
    const [pending,   setPending]   = useState([]);
    const [cancelled, setCancelled] = useState([]);              // отклонённые фантом-чеки
    const [viewMode,  setViewMode]  = useState('active');        // 'active' | 'cancelled'
    const [dayFilter, setDayFilter] = useState('today');   // 'today' | 'yesterday'
    const [loading,   setLoading]   = useState(true);
    const [endingShift, setEndingShift] = useState(false);
    const [confirmEnd, setConfirmEnd] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [admins,   setAdmins]   = useState([]);

    const [products,   setProducts]   = useState([]);
    const [discounts,  setDiscounts]  = useState([]);
    const [categories, setCategories] = useState([]);
    const [processModal, setProcessModal] = useState({ open: false, notification: null, loading: false });
    const [delReq, setDelReq] = useState({ open: false, order: null, reason: '', saving: false });

    // ── Загрузка чеков, pending- и cancelled-уведомлений и справочников ─────────
    const loadAll = useCallback(async () => {
        if (!activeShift) return true;
        setLoading(true);
        const iso = dayFilter === 'yesterday' ? businessDayISO(-1) : businessTodayISO();
        const [ordersRes, pendingRes, cancelledRes, productsRes, discountsRes, categoriesRes] = await Promise.all([
            APIGetOrders(token, {
                date_from: iso,
                date_to: iso,
                location_id: activeShift.location?.id || activeShift.location,
            }),
            APIGetPendingSaleNotifications(token, { status: 'pending', date: iso }),
            APIGetPendingSaleNotifications(token, { status: 'cancelled', date: iso }),
            APIGetProducts(token, { is_active: true }),
            APIGetDiscounts(token, { is_active: true }),
            APIGetProductCategories(token, { is_active: true, with_counts: true }),
        ]);
        if (Array.isArray(ordersRes))     setOrders(ordersRes);
        if (Array.isArray(pendingRes))    setPending(pendingRes);
        if (Array.isArray(cancelledRes))  setCancelled(cancelledRes);
        if (Array.isArray(productsRes))   setProducts(productsRes);
        if (Array.isArray(discountsRes))  setDiscounts(discountsRes);
        if (Array.isArray(categoriesRes)) setCategories(categoriesRes);
        setLoading(false);
        return true;
    }, [token, activeShift, dayFilter]);

    const load = useCallback(async () => {
        setLoading(true);
        const data = await APIWorkstationGetAdmins(token);
        if (Array.isArray(data?.admins)) setAdmins(data.admins);
        else if (Array.isArray(data))    setAdmins(data);
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    // Тихий авто-поллинг раздела чеков: подтягиваем только чеки и очередь продаж,
    // без setLoading и без перезагрузки справочников — список не мигает.
    useEffect(() => {
        if (!activeShift) return;
        if (dayFilter !== 'today') return;          // «Вчера» — данные статичны, не поллим
        const id = setInterval(async () => {
            if (document.hidden) return;
            const iso = businessTodayISO();
            const [ordersRes, pendingRes] = await Promise.all([
                APIGetOrders(token, {
                    date_from: iso,
                    date_to: iso,
                    location_id: activeShift.location?.id || activeShift.location,
                }),
                APIGetPendingSaleNotifications(token, { status: 'pending', date: iso }),
            ]);
            if (Array.isArray(ordersRes)) setOrders(ordersRes);
            if (Array.isArray(pendingRes)) setPending(pendingRes);
        }, 15000);
        return () => clearInterval(id);
    }, [activeShift, token, dayFilter]);

    // Разделённая оплата (терминал+наличные и т.п.) = ОДИН логический чек (общий receipt_uid).
    // Группируем: одна карточка на чек. Одиночные чеки — каждый сам по себе.
    const groupedOrders = useMemo(() => {
        const map = new Map();
        for (const o of orders) {
            const key = o.receipt_uid || `single-${o.id}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(o);
        }
        return [...map.values()];
    }, [orders]);

    // ── Обработчики ───────────────────────────────────────────────
    const handleEndShift = async () => {
        setEndingShift(true);
        await endShift();
        setEndingShift(false);
        setConfirmEnd(false);
    };

    const submitDeletionRequest = async () => {
        if (delReq.reason.trim().length < 3) { showToast('Укажите причину', 'warning'); return; }
        setDelReq(p => ({ ...p, saving: true }));
        const res = await APICreateOrderDeletionRequest(token, delReq.order.id, delReq.reason.trim());
        setDelReq(p => ({ ...p, saving: false }));
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось создать заявку'), 'danger'); return; }
        showToast('Заявка на удаление отправлена ✓');
        setDelReq({ open: false, order: null, reason: '', saving: false });
    };

    // Продажа уже в кассе — здесь только подтверждаем (чек НЕ пересоздаётся).
    const handleOpenProcess = async (notification) => {
        setProcessingId(notification.id);
        const res = await APIProcessSaleNotification(token, notification.id, {});
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось подтвердить продажу'), 'danger');
            setProcessingId(null);
            return;
        }
        showToast('Продажа подтверждена');
        setPending(prev => prev.filter(p => p.id !== notification.id));
        setProcessingId(null);
        loadAll();
    };

    // Подтверждение из строки: открываем окно оформления (или подтверждаем, если фантома уже нет)
    const handleRowConfirm = (order) => {
        const notif = pending.find(n => n.id === order.sale_notification_id);
        if (notif) setProcessModal({ open: true, notification: notif, loading: false });
        else handleConfirmOrder(order);
    };

    const handleCancelOrder = async (o) => {
        setProcessingId(o.id);
        const res = await APICancelOrder(token, o.id);
        if (res?.error) showToast(parseApiError(res.error, 'Не удалось отменить чек'), 'danger');
        else { showToast('Чек отменён'); loadAll(); }
        setProcessingId(null);
    };

    // Удаление из строки: подтверждённый → заявка на удаление; неоформленный (фантом) → отклонение
    const handleRowDelete = async (order) => {
        if (order.confirmation_status === 'pending') {
            const notif = pending.find(n => n.id === order.sale_notification_id);
            if (!notif) return;
            if (!window.confirm('Отклонить неоформленный чек?')) return;
            setProcessingId(order.id);
            const res = await APICancelSaleNotification(token, notif.id);
            if (res?.error) showToast(parseApiError(res.error, 'Не удалось отклонить'), 'danger');
            else { showToast('Чек отклонён'); loadAll(); }
            setProcessingId(null);
        } else {
            setDelReq({ open: true, order, reason: '', saving: false });
        }
    };

    // Подтвердить продажу (чек уже в кассе — просто помечаем)
    const handleConfirmOrder = async (o) => {
        if (!o.sale_notification_id) return;
        setProcessingId(o.id);
        const res = await APIProcessSaleNotification(token, o.sale_notification_id, {});
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось подтвердить'), 'danger');
        } else {
            showToast('Продажа подтверждена');
            loadAll();
        }
        setProcessingId(null);
    };

    // Удалить чек (сервер сам отменит связанный фантом)
    const handleDeleteOrder = async (o) => {
        setProcessingId(o.id);
        const res = await APIDeleteOrder(token, o.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось удалить чек'), 'danger');
        } else {
            showToast('Чек удалён');
            loadAll();
        }
        setProcessingId(null);
    };

    // Отправить обработанный чек на сервер.
    // payload = { assignments: [...] } из ProcessSaleModal (новый формат).
    const handleSubmitProcess = async (payload) => {
        const notification = processModal.notification;
        if (!notification) return;
        setProcessModal(p => ({ ...p, loading: true }));
        const res = await APIProcessSaleNotification(token, notification.id, payload);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось оформить чек'), 'danger');
            setProcessModal(p => ({ ...p, loading: false }));
            return;
        }
        const total = (res.orders || []).reduce((s, o) => s + parseFloat(o.value || 0), 0);
        showToast(`Чек оформлен · ${total.toLocaleString('ru-RU')} ₽`);
        setPending(prev => prev.filter(p => p.id !== notification.id));
        setProcessModal({ open: false, notification: null, loading: false });
        loadAll();
    };

    const handleCancelNotif = async (notification) => {
        setProcessingId(notification.id);
        const res = await APICancelSaleNotification(token, notification.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось отменить'), 'danger');
        } else {
            showToast('Уведомление отменено');
            setPending(prev => prev.filter(p => p.id !== notification.id));
            loadAll();
        }
        setProcessingId(null);
    };

    // Восстановление отклонённого фантом-чека: статус cancelled → pending,
    // карточка переезжает из «Отклонённых» в «Активные → Ожидают оформления».
    const handleRestoreNotif = async (notification) => {
        setProcessingId(notification.id);
        const res = await APIRestoreSaleNotification(token, notification.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось восстановить'), 'danger');
        } else {
            showToast('Уведомление восстановлено — можно оформлять');
            setCancelled(prev => prev.filter(p => p.id !== notification.id));
            setViewMode('active');
            loadAll();
        }
        setProcessingId(null);
    };

    // ── Статистические значения ───────────────────────────────────
    const today = stats?.today || {};
    const shift = stats?.shift || activeShift || {};
    const revByType = today.revenue_by_type || {};

    const statBlocks = useMemo(() => ([
        {
            label: 'Выручка', value: fmtMoney(today.revenue_total),
            sub: `${today.orders_count || 0} чек.`,
            color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)',
            icon: <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />,
        },
        {
            label: 'Перевод', value: fmtMoney(revByType.transfer),
            color: '#818cf8', bg: 'rgba(129,140,248,0.08)',
            icon: <CreditCardRoundedIcon sx={{ fontSize: 18 }} />,
        },
        {
            label: 'Наличные', value: fmtMoney(revByType.cash),
            color: '#22c55e', bg: 'rgba(34,197,94,0.08)',
            icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} />,
        },
        {
            label: 'Терминал', value: fmtMoney(revByType.terminal),
            color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',
            icon: <PointOfSaleRoundedIcon sx={{ fontSize: 18 }} />,
        },
    ]), [today, revByType]);
    
    // ── Если смена не начата → экран выбора админа ────────────────
    if (!activeShift) {
        return (
            <motion.div
                variants={pageVariants} initial="initial" animate="animate" exit="exit"
                style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
            >
                <Header />
                <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                            Рабочая точка
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            Смена не начата
                        </Typography>
                    </Box>
                    <StartShiftPanel onStart={startShift} />
                </Box>
            </motion.div>
        );
    }

    return (
        <motion.div
            variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />
            <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* Шапка */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton variant="plain" onClick={() => navigate('/workstation')}>
                            <ArrowBackIosRoundedIcon />
                        </IconButton>
                        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
                            История чеков
                        </Typography>
                    </Box>
                    <Button
                        onClick={() => setAddOrderOpen(true)}
                        startDecorator={<AddRoundedIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                            color: '#fff', fontWeight: 700,
                        }}
                    >
                        Добавить чек
                    </Button>
                </Box>

                {/* Плашка «За сегодня / Вчера» */}
                <Box sx={{ display: 'flex', gap: 0.8, mb: 1.2 }}>
                    {[
                        { key: 'today',     label: 'За сегодня' },
                        { key: 'yesterday', label: 'Вчера' },
                    ].map(d => (
                        <Chip key={d.key} size="sm"
                            variant={dayFilter === d.key ? 'solid' : 'soft'}
                            color={dayFilter === d.key ? 'primary' : 'neutral'}
                            onClick={() => setDayFilter(d.key)}
                            sx={{ cursor: 'pointer', fontWeight: 700 }}>
                            {d.label}
                        </Chip>
                    ))}
                </Box>

                {/* Свитч «Активные / Отклонённые» */}
                <Box sx={{
                    display: 'flex',
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-lg)',
                    p: 0.4,
                    mb: 2,
                }}>
                    {[
                        { key: 'active',    label: 'Активные',    count: pending.length + groupedOrders.length },
                        { key: 'cancelled', label: 'Отклонённые', count: cancelled.length },
                    ].map(tab => (
                        <Box
                            key={tab.key}
                            onClick={() => setViewMode(tab.key)}
                            sx={{
                                flex: 1,
                                textAlign: 'center',
                                py: 0.8,
                                borderRadius: 'var(--joy-radius-md)',
                                cursor: 'pointer',
                                transition: 'all .15s',
                                bgcolor: viewMode === tab.key
                                    ? 'var(--joy-palette-primary-500)'
                                    : 'transparent',
                                color: viewMode === tab.key
                                    ? '#fff'
                                    : 'var(--joy-palette-text-secondary)',
                                fontWeight: viewMode === tab.key ? 800 : 600,
                                fontSize: 13,
                            }}
                        >
                            {tab.label}
                            {tab.count > 0 && (
                                <Chip size="sm" sx={{
                                    ml: 0.8,
                                    bgcolor: viewMode === tab.key ? 'rgba(255,255,255,0.22)' : 'var(--joy-palette-background-level2)',
                                    color:   viewMode === tab.key ? '#fff' : 'var(--joy-palette-text-tertiary)',
                                    fontSize: 11, fontWeight: 800,
                                }}>
                                    {tab.count}
                                </Chip>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* === РЕЖИМ: АКТИВНЫЕ ============================================ */}
                {viewMode === 'active' && (
                    <>
                        {/* Pending-уведомления (фантом-чеки от фотографов) */}
                        {pending.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                                    <NotificationsActiveRoundedIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Ожидают оформления
                                    </Typography>
                                    <Chip size="sm" sx={{ ml: 0.5, bgcolor: 'rgba(245,158,11,0.18)', color: '#f59e0b', fontSize: 11, fontWeight: 800 }}>
                                        {pending.length}
                                    </Chip>
                                </Box>
                                <Stack spacing={1}>
                                    <AnimatePresence>
                                        {pending.map(n => (
                                            <motion.div
                                                key={n.id}
                                                layout
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, x: -100 }}
                                            >
                                                <PendingSaleCard
                                                    notification={n}
                                                    mode="pending"
                                                    onProcess={handleOpenProcess}
                                                    onCancel={handleCancelNotif}
                                                    processing={processingId === n.id}
                                                />
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </Stack>
                            </Box>
                        )}

                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                            {dayFilter === 'yesterday' ? 'Чеки за вчера' : 'Все чеки за текущую смену'}
                        </Typography>
                    </>
                )}

                {/* === РЕЖИМ: ОТКЛОНЁННЫЕ ========================================= */}
                {viewMode === 'cancelled' && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }}>
                            Отклонённые фантом-чеки за сегодня. Нажмите «Оформить чек», чтобы вернуть в обработку.
                        </Typography>
                        {cancelled.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6, color: 'var(--joy-palette-text-tertiary)' }}>
                                Нет отклонённых чеков
                            </Box>
                        ) : (
                            <Stack spacing={1}>
                                <AnimatePresence>
                                    {cancelled.map(n => (
                                        <motion.div
                                            key={n.id}
                                            layout
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, x: -100 }}
                                        >
                                            <PendingSaleCard
                                                notification={n}
                                                mode="cancelled"
                                                onRestore={handleRestoreNotif}
                                                processing={processingId === n.id}
                                            />
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </Stack>
                        )}
                    </Box>
                )}

                {/* Список Order'ов (только в режиме «Активные») */}
                {viewMode === 'active' && (
                    loading ? <MemeLoader /> : orders.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, color: 'var(--joy-palette-text-tertiary)' }}>
                            Пока нет чеков за смену
                        </Box>
                    ) : (
                        <Stack spacing={1}>
                            <AnimatePresence>
                                {groupedOrders.map(group => {
                                    const head = group[0];
                                    const groupTotal = group.reduce((s, o) => s + parseFloat(o.value || 0), 0);
                                    const allItems = group.flatMap(o => o.items || []);
                                    const groupComment = group.map(o => o.comment).find(c => c && c.trim()) || '';
                                    const isPending = group.some(o => o.confirmation_status === 'pending');
                                    const busy = group.some(o => processingId === o.id);
                                    const photographerAt = head.receipt_datetime || head.date_create;
                                    const adminAt        = head.date_create;
                                    const hasTwoTimes = head.receipt_datetime
                                        && Math.abs(new Date(head.receipt_datetime).getTime() - new Date(head.date_create).getTime()) > 60_000;
                                    const groupKey = head.receipt_uid || `single-${head.id}`;

                                    return (
                                        <motion.div
                                            key={groupKey}
                                            layout
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, x: -100 }}
                                        >
                                            <CompactCheckCard
                                                group={group}
                                                onConfirm={handleRowConfirm}
                                                onDelete={handleRowDelete}
                                                onCancel={handleCancelOrder}
                                                busy={busy}
                                            />
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </Stack>
                    )
                )}

                <Modal open={delReq.open} onClose={() => setDelReq({ open: false, order: null, reason: '', saving: false })}>
                    <ModalDialog sx={{
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)', maxWidth: 420, width: '100%', p: 3,
                    }}>
                        <ModalClose />
                        <Typography sx={{ fontSize: 17, fontWeight: 800, mb: 0.5 }}>Удалить чек</Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 1.5 }}>
                            Заявку рассмотрит сотрудник с правом на удаление. Укажите причину.
                        </Typography>
                        <Textarea minRows={2} placeholder="Причина (например: неправильно оформлен тип оплаты)"
                            value={delReq.reason}
                            onChange={(e) => setDelReq(p => ({ ...p, reason: e.target.value }))}
                            sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button color="danger" onClick={submitDeletionRequest} loading={delReq.saving} sx={{ flex: 1, fontWeight: 800 }}>
                                Отправить заявку
                            </Button>
                            <Button variant="outlined" onClick={() => setDelReq({ open: false, order: null, reason: '', saving: false })}
                                sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)' }}>
                                Отмена
                            </Button>
                        </Box>
                    </ModalDialog>
                </Modal>
            </Box>

            {/* Модалка добавления */}
            <WorkstationAddOrderModal
                open={addOrderOpen}
                onClose={() => setAddOrderOpen(false)}
                onCreated={loadAll}
                locationId={activeShift?.location?.id || activeShift?.location}
                canSetDate={hasPerm('finance.orders.set_date')}
            />

            {/* Модалка оформления чека по фантому */}
            <ProcessSaleModal
                open={processModal.open}
                notification={processModal.notification}
                products={products}
                discounts={discounts}
                categories={categories}
                loading={processModal.loading}
                onClose={() => setProcessModal({ open: false, notification: null, loading: false })}
                onSubmit={handleSubmitProcess}
                canSetDate={hasPerm('finance.orders.set_date')}
                admins={admins}
                defaultAdminId={processModal.notification?.default_admin_id ?? null}
            />
        </motion.div>
    );
}